import * as LDClient from 'ldclient-js';
import _, {isEmpty} from 'lodash';
import {Store} from 'redux';

import {isDevelopment, isProduction, isStaging} from '../../libs/shared/helpers/src/environment/envHelpers';
import {isOnline} from '../../srcCommon/helpers/networkReachabilityHelpers';
import {reportToBugsnag} from '../../srcCommon/services/bugsnag/bugsnag';
import {isGlobalAdmin} from '../helpers/models/accessHelpers';
import {trackEvent, TrackingTypesEnum} from '../services/tracking';
import {AppState} from '../store/appState';
import {updateFeatureFlags} from '../store/featureFlags/featureFlagsActions';

/*
 * Constants.
 */

// For debugging.
const hasRuntimeFeatureFlagsInDevelopment = false;

/*
 * State.
 */

const requestedFlags = new Map<string, unknown>();
let appStore: Store<AppState> | undefined;
let runtimeClient: LDClient.LDClient | undefined;

/*
 * Setup.
 */

export function setupFeatureFlags(store: Store<AppState>) {
  appStore = store;
  const state = store.getState();
  const {isEnabled, flags} = state.data.config.launchdarkly;

  // Temporary instrumentation to troubleshoot https://frontjira.atlassian.net/browse/PB-12802
  if (isProduction() || isStaging()) {
    if (!isEnabled) {
      reportToBugsnag(new Error(`Feature flags disabled in boot data for teammate ${state.data.userId}`));
    } else if (!flags) {
      reportToBugsnag(new Error(`Feature flags missing in boot data for teammate ${state.data.userId}`));
    }
  }

  if (!isEnabled) {
    return;
  }

  // No need to wait for this to complete.
  setupRuntimeFeatureFlagsAsync(store);
}

export function reconnectRuntimeFeatureFlagsAsync() {
  // TODO: Re-enable this on mobile. And remove FF update action from refreshBootAction in dataActions.ts
  if (!appStore) {
    reportToBugsnag(new Error('Trying to reconnect runtime feature flags with no app store'));
    return;
  }
  setupRuntimeFeatureFlagsAsync(appStore);
}

async function setupRuntimeFeatureFlagsAsync(store: Store<AppState>) {
  // Runtime feature flags won't work while offline.
  if (!isOnline()) {
    return;
  }

  // Don't connect to LaunchDarkly in development.
  if (isDevelopment() && !hasRuntimeFeatureFlagsInDevelopment) {
    return;
  }

  const newRuntimeClient = await makeRuntimeClient(store.getState());

  // Co-locate the clean up of the old client with the assignment of the new client.
  if (runtimeClient) {
    runtimeClient.close().catch((error) => {
      reportToBugsnag(error);
    });
  }

  runtimeClient = newRuntimeClient;

  try {
    await runtimeClient.waitForInitialization();
  } catch (error) {
    reportToBugsnag(error);
    return;
  }

  // Update the feature flags since we may have missed an update.
  const newFlags = runtimeClient.allFlags();
  store.dispatch(updateFeatureFlags(newFlags));

  runtimeClient.on('change', (changes: LDClient.LDFlagChangeset) => {
    const newFeatureFlags = _(changes)
      .toPairs()
      // Skip the initial call, where the change record only has a 'previous' prop.
      .filter(([, change]) => 'current' in change)
      // Extract the new value from the 'current' prop.
      .map(([name, change]) => [name, change.current])
      .fromPairs()
      .value();

    if (isEmpty(newFeatureFlags)) {
      return;
    }

    store.dispatch(updateFeatureFlags(newFeatureFlags));
  });
}

/*
 * API.
 */

export function trackFlagRequested(flag: string, flagValue: any) {
  if (requestedFlags.get(flag) === flagValue) {
    return;
  }

  requestedFlags.set(flag, flagValue);
  trackEvent(TrackingTypesEnum.FLAG_REQUESTED, flag, flagValue);
}

/*
 * Helpers.
 */

async function makeRuntimeClient(state: AppState): Promise<LDClient.LDClient> {
  // Initialize the LaunchDarkly client.
  const {flags, clientSideId, userHash} = state.data.config.launchdarkly;
  const {userId, teammates, access, company} = state.data;
  const user = teammates[userId];
  if (!user) {
    throw new Error('User not found.');
  }

  const launchDarklyUser = {
    key: userId.toString(),
    custom: {
      isAdmin: isGlobalAdmin(access),
      companyCreatedAt: company.createdAt,
      teammateCreatedAt: user.createdAt,
      companyId: company.id,
    },
  };
  return LDClient.initialize(clientSideId, launchDarklyUser, {
    privateAttributeNames: ['firstName', 'lastName', 'email'],
    hash: userHash,
    bootstrap: flags,
  });
}
